/**
 * The call log, grouped by who was on the other end.
 *
 * A desk that rings one customer four times in a morning shows four rows, and
 * only the last of them is still worth doing anything about. The threaded list
 * folds those four into one line and leaves the rest of the log where it was.
 *
 * WHAT COUNTS AS THE SAME NUMBER. The key is the counterpart put through
 * `normalizeE164`, so "+372 5551 2345", "003725551234" and "5551 2345" are one
 * caller rather than three. A string the normalizer refuses keeps its own
 * trimmed text as the key: it still meets its exact twin, and it does not fall
 * into one bucket with every other unreadable value.
 *
 * A WITHHELD CALLER IS NEVER GROUPED. Twilio sends no `From` when the caller id
 * is blocked, and a group headed "unknown" would be a claim that those calls
 * came from one person. They did not, so each keeps a thread of its own and
 * draws as the plain row it always was.
 *
 * Order is the order the list arrived in. The server sorts newest first, a
 * thread takes the position of its newest call, and the calls inside it keep
 * their order. Sorting again here would move rows under somebody reading them.
 *
 * Pure on purpose: no React and no component import, so the rule that decides
 * what is one caller can be tested without a browser.
 */
import { normalizeE164 } from "@wa/core/phone";
import { callCounterpart, type CallRow } from "../../../../lib/calls";

export interface CallThread {
  /** React key, and the id the open/closed set holds. */
  key: string;
  /** The other party, E.164 where it could be read, "" when it was withheld. */
  number: string;
  /** Newest first, in the order the list handed them over. Never empty. */
  rows: CallRow[];
}

/** What the folded line has to say for itself while the thread is shut. */
export interface CallThreadSummary {
  /** The newest call - what the flat list would have drawn in this slot. */
  latest: CallRow;
  /** Calls nobody picked up. The number that decides whether to open the group. */
  missed: number;
  /** Talk time across the whole thread, in seconds. */
  seconds: number;
  /** The best name we hold: the newest call in the thread that carries one. */
  companyName: string | null;
}

/** Group a page of calls by the number on the other end. */
export function groupCallsByNumber(rows: CallRow[]): CallThread[] {
  const threads: CallThread[] = [];
  const byKey = new Map<string, CallThread>();

  for (const row of rows) {
    const raw = callCounterpart(row).trim();
    const number = raw ? (normalizeE164(raw) ?? raw) : "";
    // A withheld caller gets a key nothing else can match, so it lands in a
    // thread of one and is drawn as an ordinary row.
    const key = number || `withheld:${row.id}`;
    const found = byKey.get(key);
    if (found) {
      found.rows.push(row);
      continue;
    }
    const thread: CallThread = { key, number, rows: [row] };
    byKey.set(key, thread);
    threads.push(thread);
  }

  return threads;
}

/**
 * Which thread holds this call, or null.
 *
 * The list needs it to open the group a linked call is buried in. A detail pane
 * showing a call whose row is nowhere on screen reads as the list having lost
 * it, and the bell links straight at a call id.
 */
export function threadKeyOf(threads: CallThread[], callId: string | null): string | null {
  if (!callId) return null;
  for (const thread of threads) {
    if (thread.rows.some((row) => row.id === callId)) return thread.key;
  }
  return null;
}

/**
 * The facts the folded line prints, worked out once.
 *
 * "Missed" is the pair `CallItem` draws the red tile for - rung out and busy.
 * A blocked caller is deliberately not counted: the desk turned it away on
 * purpose, and counting it here would put an alarm on the very rows the
 * blocklist exists to silence.
 */
export function summarizeThread(thread: CallThread): CallThreadSummary {
  // Never empty: `groupCallsByNumber` creates a thread from a row, so the
  // first one is always there.
  const latest = thread.rows[0]!;
  let missed = 0;
  let seconds = 0;
  let companyName: string | null = null;
  for (const row of thread.rows) {
    if (row.status === "no-answer" || row.status === "busy") missed += 1;
    seconds += row.durationSec ?? 0;
    if (!companyName && row.companyName) companyName = row.companyName;
  }
  return { latest, missed, seconds, companyName };
}
